import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { CompanyProfile, fetchPublicCompanyProfile } from '@/services/companyProfile';
import { OperationLocation, fetchPublicOperationLocations } from '@/services/operationLocations';

const fallbackCompanyProfile: CompanyProfile = {
  companyName: '富聆聽健康',
  servicePhone: '0800-123-456',
  servicePhoneNote: '免付費',
  serviceHours: '週一至週五 09:00 - 18:00',
  serviceEmail: 'service@fuling.com',
  addressCity: '台北市',
  addressDistrict: '信義區',
  addressPostalCode: '110',
  addressLine: '松高路 88 號',
  contactPhone: null,
  smsPhone: null,
  copyright: null,
  lineId: '@fulinghealth',
  lineUrl: null,
  facebookUrl: null,
  instagramUrl: null,
  youtubeUrl: null,
};

const fallbackStores: OperationLocation[] = [
  {
    id: -1,
    name: '台北大安旗艦店',
    tag: '總店',
    isHeadquarters: true,
    addressCity: '台北市',
    addressDistrict: '大安區',
    addressPostalCode: '',
    addressLine: '忠孝東路四段 100 號 2 樓',
    phone: '02-2345-6789',
    businessHours: ['週一至週五 09:00 - 18:00', '週六 10:00 - 17:00'],
  },
  {
    id: -2,
    name: '台北信義店',
    tag: '分店',
    isHeadquarters: false,
    addressCity: '台北市',
    addressDistrict: '信義區',
    addressPostalCode: '',
    addressLine: '松壽路 18 號 B1',
    phone: '02-2345-6790',
    businessHours: ['週一至週五 09:00 - 18:00', '週六 10:00 - 17:00'],
  },
  {
    id: -3,
    name: '台中店',
    tag: '分店',
    isHeadquarters: false,
    addressCity: '台中市',
    addressDistrict: '西區',
    addressPostalCode: '',
    addressLine: '公益路 168 號 3 樓',
    phone: '04-2345-6789',
    businessHours: ['週一至週五 09:00 - 18:00'],
  },
];

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>(fallbackCompanyProfile);
  const [stores, setStores] = useState<OperationLocation[]>(fallbackStores);

  useEffect(() => {
    let alive = true;
    fetchPublicCompanyProfile().then((profile) => {
      if (alive && profile) setCompanyProfile(profile);
    });
    fetchPublicOperationLocations('Contact').then((countries) => {
      if (!alive) return;
      const locations = countries.flatMap((country) => country.locations);
      if (locations.length > 0) setStores(locations);
    });
    return () => {
      alive = false;
    };
  }, []);

  const headquarters = stores.find((store) => store.isHeadquarters) || stores[0];
  const mapQuery = headquarters
    ? encodeURIComponent(`${companyProfile.companyName} ${headquarters.addressCity}${headquarters.addressDistrict}${headquarters.addressLine}`)
    : encodeURIComponent(companyProfile.companyName);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/contact-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Name: formData.name,
          Email: formData.email,
          Subject: formData.subject,
          Message: formData.message,
          PageUrl: window.location.href,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.ok !== false) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitError(data.message || '訊息送出失敗，請稍後再試。');
        setSubmitStatus('error');
      }
    } catch {
      setSubmitError('訊息送出失敗，請稍後再試。');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* 頁面標題 */}
        <div className="relative overflow-hidden text-white py-12 md:py-16 animate-fade-in-up">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E40] via-[#256F4A] to-[#1B3D2A]" />
          <div className="absolute -top-20 -right-20 w-72 h-72 md:w-96 md:h-96 rounded-full bg-[#D4A574]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#2E8B57]/20 blur-2xl" />
          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              Get in Touch
            </span>
            <h1 className="text-3xl md:text-4xl font-bold">聯絡我們</h1>
            <p className="mt-3 text-white/80 text-base md:text-lg max-w-2xl">
              無論您有聽力保健疑問、產品諮詢或服務需求，歡迎隨時與我們聯繫。
              我們的專業團隊將在收到訊息後儘快回覆。
            </p>
          </div>
          <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>

        {/* 門市資訊 */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-16 max-w-7xl mx-auto animate-fade-in-up delay-100">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              門市據點
            </span>
            <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">門市資訊</h2>
            <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {stores.map((store) => (
              <div key={store.id} className="relative bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-8 hover:shadow-sm transition-shadow duration-300">
                {store.tag && (
                  <span className="absolute top-5 right-5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#D4A574]/15 text-[#9A7B4F]">
                    {store.tag}
                  </span>
                )}
                <div className="w-14 h-14 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                  <i className="ri-store-2-line text-3xl text-[#1B3D2A]" />
                </div>
                <h3 className="mt-4 text-base font-bold text-[#1B3D2A]">{store.name}</h3>
                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-[#9A8A7A] text-base" />
                    </div>
                    <p className="text-base text-[#5A6B5A]">{store.addressCity}{store.addressDistrict}{store.addressLine}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-phone-line text-[#9A8A7A] text-base" />
                    </div>
                    <p className="text-base text-[#5A6B5A]">{store.phone}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-time-line text-[#9A8A7A] text-base" />
                    </div>
                    <div>
                      {(store.businessHours.length > 0 ? store.businessHours : ['-']).map((line, index) => (
                        <p key={index} className="text-base text-[#9A8A7A]">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 聯絡表單 + 地圖 */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-16 bg-[#FAF8F5] animate-fade-in-up delay-200">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-16">
            {/* 左側 - 表單 */}
            <div className="lg:w-1/2">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                線上留言
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">聯絡表單</h2>
              <p className="mt-2 text-base text-[#5A6B5A]">填寫以下表單，我們將儘快回覆您的問題</p>

              {submitStatus === 'success' && (
                <div className="mt-6 p-4 rounded-xl bg-[#1B3D2A]/5 border border-[#1B3D2A]/20 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-[#1B3D2A]" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-[#1B3D2A]">訊息已送出！</p>
                    <p className="text-base text-[#5A6B5A] mt-1">我們已收到您的訊息，將於 1-2 個工作天內回覆。</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                    <i className="ri-error-warning-line text-red-600" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-red-800">送出失敗</p>
                    <p className="text-base text-red-600 mt-1">{submitError || `請稍後再試，或直接撥打客服專線 ${companyProfile.servicePhone}。`}</p>
                  </div>
                </div>
              )}

              <form
                id="contact-form"
                onSubmit={handleSubmit}
                className="mt-6 space-y-4 md:space-y-5"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-base md:text-lg font-medium text-[#1B3D2A] mb-1.5 md:mb-2">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="請輸入您的姓名"
                    required
                    className="w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base md:text-lg outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-base md:text-lg font-medium text-[#1B3D2A] mb-1.5 md:mb-2">
                    電子郵件
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="例如：name@example.com"
                    required
                    className="w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base md:text-lg outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-base md:text-lg font-medium text-[#1B3D2A] mb-1.5 md:mb-2">
                    諮詢主題
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base md:text-lg outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A] bg-white cursor-pointer"
                  >
                    <option value="">請選擇諮詢主題</option>
                    <option value="聽力檢測預約">聽力檢測預約</option>
                    <option value="產品諮詢">產品諮詢</option>
                    <option value="助聽器保養">助聽器保養</option>
                    <option value="售後服務">售後服務</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-base md:text-lg font-medium text-[#1B3D2A] mb-1.5 md:mb-2">
                    訊息內容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="請描述您的問題或需求..."
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base md:text-lg outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A] resize-none"
                  />
                  <div className="text-right mt-1">
                    <span className={`text-base ${formData.message.length > 500 ? 'text-red-500' : 'text-[#9A8A7A]'}`}>
                      {formData.message.length} / 500
                    </span>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1B3D2A] text-white py-3 md:py-4 rounded-xl text-base md:text-lg font-medium hover:bg-[#143026] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      送出中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      送出訊息
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* 右側 - 地圖與聯絡方式 */}
            <div className="lg:w-1/2 flex flex-col gap-8">
              <div>
                <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                  其他聯絡方式
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">直接聯繫</h2>
                <div className="mt-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4A574]/15 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-[#D4A574]" />
                    </div>
                    <div>
                      <p className="text-base font-medium text-[#1B3D2A]">客服專線</p>
                      <p className="text-base text-[#5A6B5A] mt-0.5">
                        {companyProfile.servicePhone}
                        {companyProfile.servicePhoneNote ? `（${companyProfile.servicePhoneNote}）` : ''}
                      </p>
                      <p className="text-base text-[#9A8A7A] mt-0.5">{companyProfile.serviceHours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4A574]/15 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-[#D4A574]" />
                    </div>
                    <div>
                      <p className="text-base font-medium text-[#1B3D2A]">電子郵件</p>
                      <p className="text-base text-[#5A6B5A] mt-0.5">{companyProfile.serviceEmail}</p>
                    </div>
                  </div>
                  {companyProfile.lineId && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4A574]/15 flex items-center justify-center shrink-0">
                        <i className="ri-line-line text-[#D4A574]" />
                      </div>
                      <div>
                        <p className="text-base font-medium text-[#1B3D2A]">LINE 官方帳號</p>
                        <p className="text-base text-[#5A6B5A] mt-0.5">{companyProfile.lineId}</p>
                      </div>
                    </div>
                  )}
                  {companyProfile.contactPhone && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4A574]/15 flex items-center justify-center shrink-0">
                        <i className="ri-building-line text-[#D4A574]" />
                      </div>
                      <div>
                        <p className="text-base font-medium text-[#1B3D2A]">聯絡電話</p>
                        <p className="text-base text-[#5A6B5A] mt-0.5">{companyProfile.contactPhone}</p>
                      </div>
                    </div>
                  )}
                  {companyProfile.smsPhone && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4A574]/15 flex items-center justify-center shrink-0">
                        <i className="ri-message-3-line text-[#D4A574]" />
                      </div>
                      <div>
                        <p className="text-base font-medium text-[#1B3D2A]">簡訊服務</p>
                        <p className="text-base text-[#5A6B5A] mt-0.5">{companyProfile.smsPhone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 地圖 */}
              <div className="rounded-2xl overflow-hidden border border-[#E8E0D6] h-64 md:h-80">
                <iframe
                  src={`https://maps.google.com/maps?q=${mapQuery}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="富聆聽健康門市位置"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 底部 CTA */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-16 max-w-7xl mx-auto animate-fade-in-up delay-300">
          <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">需要立即預約聽力檢測？</h3>
              <p className="mt-3 text-lg text-[#5A6B5A] leading-relaxed">
                直接填寫預約表單，選擇您方便的日期，我們將盡快與您聯繫確認。
              </p>
            </div>
            <button
              onClick={() => navigate('/booking')}
              className="shrink-0 bg-[#1B3D2A] text-white px-6 md:px-8 py-3 md:py-3.5 rounded-xl text-base md:text-lg font-medium hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-calendar-check-line" />
              立即預約
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}